/* eslint-disable react-hooks/exhaustive-deps */
import { memo, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { ModalComponent, WalletComponent } from "@zenith/components";
import {
  LIMIT_EXCEEDED_ERROR_MODAL_BODY,
  LIMIT_EXCEEDED_ERROR_MODAL_TITLE,
  LIMIT_EXCEEDED_ERROR_MODAL_SECOND_LINE,
  DISCARD_TRANSFER_TITLE,
  DISCARD_TRANSFER_BODY,
  DISCARD_TRANSFER_BUTTON,
  UPGRADE_ACCOUNT_BUTTON,
  RETURN_TO_TRANSFER_BUTTON,
  NO_BENEFICIARY_WAS_FOUND_ERROR_CODE,
  MAX_BENEFICIARY_ACCOUNT_NUMBER_LENGTH,
  DUPLICATE_BENEFICIARY_ERROR_CODE,
  WALLET,
  SEND_MONEY_LIMIT_LABEL,
  CURRENT_ZENITH_NIGERIA_BANK_ID,
  FINOPAY_BANK_ID,
  NUMBER_INPUT_REGEX,
} from "@zenith/data/constants";
import { useSendMoneyContext } from "@zenith/context";
import {
  DASHBOARD,
  SEND_MONEY_OVERVIEW,
  UPGRADE_ACCOUNT,
} from "@zenith/routes/paths";
import { AppNavigation } from "@zenith/hooks";
import ToastSnackbarComponent from "@zenith/components/ui/ToastSnackbarComponent/toast-snackbar-component/toast-snackbar-component";
import DiscardModalComponent from "@zenith/components/ui/DiscardModalComponent/discard-modal-component/discard-modal-component";
import { useSendMoneyForm } from "../hooks/useSendMoneyForm";
import ContactSupportModal from "../components/ContactSupportModal";
import DeviceSwitchLimitBanner from "../components/DeviceSwitchLimitBanner";
import TransferOptions from "../components/TransferOptions";
import TransferForm from "../components/TransferForm";
import { INITIAL_FORM_DATA, TRANSFER_OPTIONS } from "../constants";
import { initGlobalContext } from "@zenith/context/global-context/globalContext";
import { FormDataType } from "../types";
import { sortedBeneficiaryDetailsRecord } from "@zenith/pages/BeneficiariesPage/beneficiaries-page/beneficiaries-page";
import { BeneficiaryApiService } from "@zenith/services/user";
import { handleBeneficiary } from "@zenith/utils/Helpers";
import { ERROR_MESSAGES } from "../constants";
import { RestrictedAccountComponent } from "@zenith/features/Payments/components";
import { useBalanceVisibility } from "@zenith/hooks/useBalanceVisibility";

const SendMoneyPage = memo(function SendMoneyPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateTo } = AppNavigation();
  const { sendMoneyContext, setSendMoneyContext } = useSendMoneyContext();
  const { isBalanceVisible, toggleBalanceVisibility } = useBalanceVisibility();
  const isFromOverview = location.state?.fromOverview === true;

  const {
    formData,
    formErrors,
    bankList,
    isLoadingBanks,
    bankListError,
    categoryList,
    availableLimit,
    availableBalance,
    accountNumber,
    transferOption,
    nameEnquiryRef,
    validatedAccount,
    saveBeneficiary,
    setFormData,
    setFormErrors,
    setTransferOption,
    setSaveBeneficiary,
    validateAccountNumber,
    validateAmount,
    resetAccountValidation,
    deviceSwitchLimit,
  } = useSendMoneyForm();

  const [isLimitExceeded, setIsLimitExceeded] = useState(false);
  const [showContactSupport, setShowContactSupport] = useState(false);
  const [countdown, setCountdown] = useState("");
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const [isOpenTransferModal, setIsOpenTransferModal] = useState(false);
  const [showPostNoDebitModal, setShowPostNoDebitModal] = useState(false);
  const [limitBreachToast, setLimitBreachToast] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showBeneficiaryList, setShowBeneficiaryList] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] =
    useState<sortedBeneficiaryDetailsRecord>();
  const [beneficiaryList, setBeneficiaryList] =
    useState<sortedBeneficiaryDetailsRecord[]>();
  const [isLoadingBeneficiaries, setIsLoadingBeneficiaries] = useState(false);
  const [beneficiariesError, setBeneficiariesError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidatingAccount, setIsValidatingAccount] = useState(false);

  const validateDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastValidatedKeyRef = useRef<string | null>(null);
  const hasMountedValidationCleanupRef = useRef(false);

  useEffect(() => {
    if (isFromOverview && sendMoneyContext) {
      setFormData({
        amount: sendMoneyContext.amount || 0,
        bankId: sendMoneyContext.bankId || "",
        bankName: sendMoneyContext.bankName || "",
        beneficiaryAccountNumber:
          sendMoneyContext.beneficiaryAccountNumber || "",
        beneficiaryName: sendMoneyContext.beneficiaryName || "",
        categoryId: sendMoneyContext.categoryId || "",
        categoryName: sendMoneyContext.categoryName || "",
        description: sendMoneyContext.description || "",
        accountOwner: sendMoneyContext.accountOwner || "",
      });

      if (sendMoneyContext.transferType) {
        setTransferOption(sendMoneyContext.transferType as "bank" | "wallet");
      }

      if (sendMoneyContext.saveBeneficiaryStatus !== undefined) {
        setSaveBeneficiary(
          sendMoneyContext.saveBeneficiaryStatus === "success"
        );
      }

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [isFromOverview, sendMoneyContext]);

  const loadBeneficiaries = async () => {
    setIsLoadingBeneficiaries(true);
    setBeneficiariesError(false);

    const isBankTransfer = transferOption === TRANSFER_OPTIONS.BANK;

    BeneficiaryApiService.getBeneficiary(FINOPAY_BANK_ID, isBankTransfer)
      .then((response) => {
        if ("data" in response && response.data?.beneficiaryDetails) {
          setBeneficiaryList(
            response.data.beneficiaryDetails.map((beneficiary) =>
              handleBeneficiary(beneficiary)
            )
          );
        }
      })
      .catch((error) => {
        if (error.body.internalCode === NO_BENEFICIARY_WAS_FOUND_ERROR_CODE) {
          setBeneficiaryList([]);
        } else {
          setBeneficiariesError(true);
        }
      })
      .finally(() => {
        setIsLoadingBeneficiaries(false);
      });
  };

  useEffect(() => {
    loadBeneficiaries();
  }, [transferOption]);

  useEffect(() => {
    const { beneficiaryAccountNumber, bankId } = formData;
    const isBankValid = !!bankId;
    const isBankTransfer = transferOption === TRANSFER_OPTIONS.BANK;
    const isWalletTransfer = transferOption === TRANSFER_OPTIONS.WALLET;
    const isBeneficiarySelected = !!selectedBeneficiary;
    const isAccountNumberValidLength =
      beneficiaryAccountNumber.length === MAX_BENEFICIARY_ACCOUNT_NUMBER_LENGTH;
    const isAlreadyValidated =
      !!nameEnquiryRef &&
      validatedAccount?.accountNumber === beneficiaryAccountNumber &&
      validatedAccount?.bankId === bankId;

    if (validateDebounceTimerRef.current) {
      clearTimeout(validateDebounceTimerRef.current);
      validateDebounceTimerRef.current = null;
    }

    const validationKey = `${transferOption}|${beneficiaryAccountNumber}|${
      isWalletTransfer ? "" : bankId
    }`;

    if (
      beneficiaryAccountNumber &&
      isAccountNumberValidLength &&
      ((isBankValid && isBankTransfer) || isWalletTransfer) &&
      !isBeneficiarySelected &&
      !isAlreadyValidated &&
      lastValidatedKeyRef.current !== validationKey
    ) {
      setIsValidatingAccount(true);

      validateDebounceTimerRef.current = setTimeout(() => {
        lastValidatedKeyRef.current = validationKey;
        validateAccountNumber(beneficiaryAccountNumber).finally(() => {
          setIsValidatingAccount(false);
        });
      }, 500);
    }
  }, [formData.beneficiaryAccountNumber, formData.bankId, transferOption, selectedBeneficiary]);


  useEffect(() => {
    if (!hasMountedValidationCleanupRef.current) {
      hasMountedValidationCleanupRef.current = true;
      return;
    }

    const isStillValidated =
      !!nameEnquiryRef &&
      validatedAccount?.accountNumber === formData.beneficiaryAccountNumber &&
      validatedAccount?.bankId === formData.bankId;

    if (
      !isStillValidated &&
      !selectedBeneficiary &&
      (formData.beneficiaryName || nameEnquiryRef || validatedAccount)
    ) {
      setFormData((prev) => ({ ...prev, beneficiaryName: "" }));
      resetAccountValidation();
    }
  }, [formData.beneficiaryAccountNumber, formData.bankId]);

  useEffect(() => {
    return () => {
      if (validateDebounceTimerRef.current) {
        clearTimeout(validateDebounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const expiryDateTime = deviceSwitchLimit?.expiryDateTime;
    if (!expiryDateTime) return;

    const updateCountdown = () => {
      const now = new Date();
      const expiry = new Date(expiryDateTime);
      const secondsLeft = Math.max(
        0,
        Math.floor((expiry.getTime() - now.getTime()) / 1000)
      );

      if (secondsLeft === 0) {
        if (countdownRef.current) clearInterval(countdownRef.current);
        setCountdown("0:00:00");
        return;
      }

      const h = Math.floor(secondsLeft / 3600);
      const m = Math.floor((secondsLeft % 3600) / 60);
      const s = secondsLeft % 60;
      setCountdown(
        `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
      );
    };

    updateCountdown();
    countdownRef.current = setInterval(updateCountdown, 1000);

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [deviceSwitchLimit?.expiryDateTime]);

  const handleEmptyTransfer = () => {
    setFormData(INITIAL_FORM_DATA);
    setIsOpenTransferModal(false);
    navigate(DASHBOARD, { replace: true });
  };

  const handleModalClose = () => {
    setIsLimitExceeded(false);
  };

  const handleUpgradeAccountTier = () => {
    navigateTo(UPGRADE_ACCOUNT);
  };

  const handleNavigateToDashboard = () => {
    navigateTo(DASHBOARD);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    if (deviceSwitchLimit?.limit && formData.amount > deviceSwitchLimit.limit) {
      setIsLoading(false);
      return;
    }
    if (formData.amount > availableLimit) {
      setIsLimitExceeded(true);
      setLimitBreachToast(true);
      setModalMessage(
        `You have exceeded your transaction limit. Kindly upgrade your account to access higher limits`
      );
      setIsLoading(false);
      return;
    }

    try {
      // Save beneficiary before navigation so the status is in context when the overview page mounts
      let saveBeneficiaryStatusResult:
        | "success"
        | "duplicate"
        | "failed"
        | undefined;

      if (saveBeneficiary) {
        const request = {
          accountNumber: formData.beneficiaryAccountNumber,
          bankId: formData.bankId,
          bankName: formData.bankName,
          beneficiaryName: formData.beneficiaryName,
        };

        try {
          const saveBeneficiaryResponse =
            await BeneficiaryApiService.postBeneficiary(request);
          if (saveBeneficiaryResponse.success && saveBeneficiaryResponse.data) {
            saveBeneficiaryStatusResult = "success";
          }
        } catch (error) {
          const internalCode = (error as { body?: { internalCode?: string } })
            ?.body?.internalCode;
          saveBeneficiaryStatusResult =
            internalCode === DUPLICATE_BENEFICIARY_ERROR_CODE
              ? "duplicate"
              : "failed";
        }
      }

      if (transferOption === TRANSFER_OPTIONS.WALLET) {
        const baseContext = {
          accountNumber: formData.beneficiaryAccountNumber,
          accountOwner: formData.accountOwner,
          amount: formData.amount,
          description: formData.description,
          categoryId: formData.categoryId,
          categoryName: formData.categoryName,
        };

        setSendMoneyContext({
          ...sendMoneyContext,
          ...baseContext,
          transferType: WALLET.TRANSFER_TYPE,
          nameEnquiryRef: "",
          saveBeneficiaryStatus: saveBeneficiaryStatusResult,
        });

        navigate(SEND_MONEY_OVERVIEW, {
          replace: true,
          state: { nameEnquiry: "", availableBalance },
        });
      } else {
        const isAlreadyValidated =
          !!nameEnquiryRef &&
          validatedAccount?.accountNumber ===
            formData.beneficiaryAccountNumber &&
          validatedAccount?.bankId === formData.bankId;

        let resolvedNameEnquiryRef = nameEnquiryRef;

        if (!isAlreadyValidated) {
          const response = await BeneficiaryApiService.getBankBeneficiary(
            formData.beneficiaryAccountNumber,
            formData.bankId
          );

          if (
            !response?.success ||
            (!response.data?.nameEnquiryRef &&
              formData.bankId !== CURRENT_ZENITH_NIGERIA_BANK_ID)
          ) {
            setFormErrors({
              ...formErrors,
              beneficiaryAccountNumber: {
                hasError: true,
                message: "Unable to validate account. Please try again.",
              },
            });
            return;
          }

          resolvedNameEnquiryRef = response.data?.nameEnquiryRef || "";
        }

        const baseContext = {
          accountNumber: formData.beneficiaryAccountNumber,
          accountOwner: formData.accountOwner,
          amount: formData.amount,
          description: formData.description,
          categoryId: formData.categoryId,
          categoryName: formData.categoryName,
        };

        setSendMoneyContext({
          ...sendMoneyContext,
          ...baseContext,
          bankId: formData.bankId,
          bankName: formData.bankName,
          transferType: "bank",
          nameEnquiryRef: resolvedNameEnquiryRef || "",
          saveBeneficiaryStatus: saveBeneficiaryStatusResult,
        });

        navigate(SEND_MONEY_OVERVIEW, {
          replace: true,
          state: {
            nameEnquiry: resolvedNameEnquiryRef || "",
            availableBalance,
          },
        });
      }
    } catch (error) {
      console.error("Error during form submission:", error);
      setFormErrors({
        ...formErrors,
        beneficiaryAccountNumber: {
          hasError: true,
          message: "Unable to process request. Please try again.",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormDataChange = (
    field: keyof FormDataType,
    value: string | number
  ) => {
    if (field === "beneficiaryAccountNumber") {
      const accountNumber = String(value).replace(NUMBER_INPUT_REGEX, "");

      setFormErrors((prev) => ({
        ...prev,
        beneficiaryAccountNumber: { hasError: false, message: "" },
      }));

      if (accountNumber === formData.beneficiaryAccountNumber) return;

      if (selectedBeneficiary) {
        setSelectedBeneficiary(undefined);
      }

      resetAccountValidation();
      lastValidatedKeyRef.current = null;
      if (validateDebounceTimerRef.current) {
        clearTimeout(validateDebounceTimerRef.current);
        validateDebounceTimerRef.current = null;
      }
      setIsValidatingAccount(false);

      const isWalletTransfer = transferOption === TRANSFER_OPTIONS.WALLET;

      setFormData((prev) => ({
        ...prev,
        beneficiaryAccountNumber: accountNumber,
        beneficiaryName: "",
        ...(isWalletTransfer ? { bankId: "", bankName: "" } : {}),
      }));
      return;
    }

    const changedBankForSelectedBeneficiary =
      field === "bankId" &&
      !!selectedBeneficiary &&
      String(value) !== String(selectedBeneficiary.bankId);

    if (changedBankForSelectedBeneficiary) {
      setSelectedBeneficiary(undefined);
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(changedBankForSelectedBeneficiary ? { beneficiaryName: "" } : {}),
    }));
  };

  const isInfoLoaded = () => {
    return availableLimit !== undefined && availableBalance !== undefined;
  };

  const handleShowBeneficiaryList = () => {
    setShowBeneficiaryList(!showBeneficiaryList);
  };

  const handleSelectedBeneficiary = (
    beneficiary: sortedBeneficiaryDetailsRecord
  ) => {
    setSelectedBeneficiary(beneficiary);
    setFormErrors({
      ...formErrors,
      beneficiaryAccountNumber: {
        hasError: false,
        message: "",
      },
    });
    const { bankName, accountNumber, bankId, beneficiaryName } = beneficiary;

    if (bankName && accountNumber && bankId && beneficiaryName) {
      setFormData((prev) => ({
        ...prev,
        bankName,
        beneficiaryName,
        bankId: String(bankId),
        beneficiaryAccountNumber: accountNumber,
      }));
      setSendMoneyContext({
        ...sendMoneyContext,
        bankName,
        beneficiaryName,
        bankId: String(bankId),
        beneficiaryAccountNumber: accountNumber,
      });
    }
    setShowBeneficiaryList(false);
  };

  const handleTransferOptionChange = (option: "bank" | "wallet") => {
    setShowBeneficiaryList(false);
    setSaveBeneficiary(false);
    setSelectedBeneficiary(undefined);
    lastValidatedKeyRef.current = null;

    setFormData(INITIAL_FORM_DATA);

    setTransferOption(option);

    setSendMoneyContext({
      ...initGlobalContext.sendMoneyContext,
      transferType: option,
    });
    setFormErrors((prev) => ({
      ...prev,
      beneficiaryAccountNumber: {
        hasError: false,
        message: "",
      },
      bankName: {
        hasError: false,
        message: "",
      },
    }));
  };

  return (
    <div className="w-full">
      <ModalComponent
        title={LIMIT_EXCEEDED_ERROR_MODAL_TITLE}
        body={LIMIT_EXCEEDED_ERROR_MODAL_BODY}
        secondLine={LIMIT_EXCEEDED_ERROR_MODAL_SECOND_LINE}
        showModal={isLimitExceeded}
        onClickClose={handleModalClose}
        onClickButton={handleModalClose}
        doubleOptions
        leftButtonText={DISCARD_TRANSFER_BUTTON}
        rightButtonText={UPGRADE_ACCOUNT_BUTTON}
        onLeftButtonClick={() => setIsLimitExceeded(false)}
        onRightButtonClick={handleUpgradeAccountTier}
      />

      <DiscardModalComponent
        isOpen={isOpenTransferModal}
        onClose={() => setIsOpenTransferModal(false)}
        primaryAction={handleEmptyTransfer}
        secondaryAction={() => setIsOpenTransferModal(false)}
        title={DISCARD_TRANSFER_TITLE}
        bodyText={DISCARD_TRANSFER_BODY}
        primaryActionText={DISCARD_TRANSFER_BUTTON}
        secondaryActionText={RETURN_TO_TRANSFER_BUTTON}
      />

      <ContactSupportModal
        open={showContactSupport}
        onClose={() => setShowContactSupport(false)}
      />

      <div className="mb-3 flex-col items-start justify-between">
        {deviceSwitchLimit && (
          <DeviceSwitchLimitBanner
            countdown={countdown}
            onContactSupportClick={() => setShowContactSupport(true)}
          />
        )}

        <div className="xl:3/5 mx-2 mt-5 w-full lg:w-4/5">
          <div className=" rounded-2.5xl bg-white py-6 px-2 md:px-8">
            <div className="flex-col justify-between">
              <h1 className="mb-8 text-2xl font-medium text-neutral-900">
                Send Money
              </h1>
              <h2 className="mb-4 text-xl font-medium text-neutral-900">
                From
              </h2>
            </div>

            <WalletComponent
              availableBalance={availableBalance}
              isInfoLoaded={isInfoLoaded}
              availableLimit={availableLimit}
              accountNumber={accountNumber}
              isBalanceVisible={isBalanceVisible}
              toggleBalanceVisibility={toggleBalanceVisibility}
            />

            <h2 className="mt-6 mb-4 text-xl font-medium text-neutral-900">
              Transfer Option
            </h2>

            <TransferOptions
              selectedOption={transferOption}
              onOptionChange={handleTransferOptionChange}
            />

            <h2 className="mt-6 mb-4 text-xl font-medium text-neutral-900">
              To
            </h2>

            <TransferForm
              formData={formData}
              formErrors={formErrors}
              bankList={bankList}
              isLoadingBanks={isLoadingBanks}
              bankListError={bankListError}
              categoryList={categoryList}
              beneficiaryList={beneficiaryList}
              selectedBeneficiary={selectedBeneficiary}
              isLoadingBeneficiaries={isLoadingBeneficiaries}
              beneficiariesError={beneficiariesError}
              showBeneficiaryList={showBeneficiaryList}
              availableLimit={availableLimit}
              availableBalance={availableBalance}
              isLoading={isLoading}
              transferOption={transferOption}
              saveBeneficiary={saveBeneficiary}
              setFormData={setFormData}
              onSubmit={handleSubmit}
              onTransferOptionChange={handleTransferOptionChange}
              onSaveBeneficiaryChange={(e) =>
                setSaveBeneficiary(e.target.checked)
              }
              onFormDataChange={handleFormDataChange}
              onBeneficiarySelect={handleSelectedBeneficiary}
              onShowBeneficiaryList={handleShowBeneficiaryList}
              validateAccountNumber={validateAccountNumber}
              validateAmount={validateAmount}
              isValidating={isValidatingAccount}
            />
          </div>
        </div>

        <RestrictedAccountComponent type="send-money" />

        <ModalComponent
          title={SEND_MONEY_LIMIT_LABEL}
          body={modalMessage}
          showModal={showPostNoDebitModal}
          onClickButton={handleNavigateToDashboard}
          onClickClose={handleNavigateToDashboard}
          doubleOptions={true}
          leftButtonText={DISCARD_TRANSFER_BUTTON}
          rightButtonText={UPGRADE_ACCOUNT_BUTTON}
          onLeftButtonClick={handleNavigateToDashboard}
          onRightButtonClick={handleUpgradeAccountTier}
        />
      </div>

      {limitBreachToast && (
        <ToastSnackbarComponent
          horizontal="center"
          vertical="top"
          status="error"
          text={ERROR_MESSAGES.DEBIT_LIMIT_BREACH}
          title="Debit Limit Breach"
          size="lg"
          hasAutoHideDuration
        />
      )}
    </div>
  );
});

export default SendMoneyPage;
